import { useState } from 'react'
import { useForm } from 'react-hook-form';
import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import { GeneralModal } from '../components-molecules';
import { ButtonWithLoadingIndicator } from '../components-atoms';
import { enqueueUpcImport, IValidateResult, IValidateSummary, validateUpcImport } from '../redux/actions/upc';
import ControlledDragAndDropInput from '../components-atoms/ControlledDragAndDropInput';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningOutlineIcon from '@material-ui/icons/WarningOutlined';
import InfoOutlineIcon from '@material-ui/icons/InfoOutlined';

export interface IUPCImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}

export default function UPCImportModal(props) {
  const classes = useStyles();
  const { isOpen } = props;
  const {
    setValue,
    watch
  } = useForm<any>({ reValidateMode: "onChange", mode: "onChange" });
  const [validateLoading, setValidateLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [result, setResult] = useState<IValidateResult>();
  const [importId, setImportId] = useState<string>();
  const [importMsg, setImportMsg] = useState<IValidateSummary[]>();
  const [queueMsg, setqQeueMsg] = useState<string>();
  const files = watch('File');
  const isValidated = result === "Success";

  const resetState = () => {
    setValidateLoading(false);
    setQueueLoading(false);
    setImportId(undefined);
    setImportMsg(undefined);
    setqQeueMsg(undefined);
    setValue("File", undefined, { shouldDirty: false, shouldValidate: false });
  }

  const onClose = () => {
    resetState();
    props.onClose();
  }

  const onDone = () => {
    resetState();
    props.onDone();
  }

  const onValidate = (data) => {
    setValidateLoading(true);
    const formData = new FormData();
    if (files && files.length > 0) {
      formData.append("File", files[0]);
      validateUpcImport(formData, (res) => {
        setImportId(res.Id);
        setImportMsg(res.Summary);
        setResult(res.Result);
        setValidateLoading(false);
      },
        (err) => {
          console.log('Err', err);
          setValidateLoading(false);
        });
    }
  }

  const onQueue = () => {
    if (!importId) return;
    setQueueLoading(true);
    enqueueUpcImport(importId, (res) => {
      setqQeueMsg(res);
      setQueueLoading(false);
    }, (err) => {
      console.log('Err', err);
      setQueueLoading(false);
    })
  }

  const renderTitle = () => {
    if (!result) <></>;

    if (result === "Warning") {
      return (
        <>
          <WarningOutlineIcon fontSize="large" className={classes.warning} />
          <Typography variant="h6" color="textSecondary">
            Validation passed with warnings
          </Typography>
        </>
      )
    }
    if (result === "Failure") {
      return (
        <>
          <ErrorOutlineIcon fontSize="large" className={classes.error} />
          <Typography variant="h6" color="textSecondary">
            Validation failed
          </Typography>
        </>
      )
    }

    if (result === "Success") {
      return (
        <>
          <CheckCircleOutlineIcon fontSize="large" className={classes.success} />
          <Typography variant="h6" color="textSecondary">
            Validation passed
          </Typography>
        </>
      )
    }
  }

  const renderContent = () => {
    if (queueMsg) {
      return (
        <Grid item xs={12} className={classes.queueMsg}>
          <CheckCircleOutlineIcon fontSize="large" className={classes.success} />
          <Typography variant="h6" color="textSecondary" align="center">
            {queueMsg}
          </Typography>
        </Grid>
      )
    }

    if (importMsg && importMsg.length > 0) {
      return (
        <Grid item xs={12}>
          {renderTitle()}
          <hr className={classes.separator} />
          <div className={classes.summary}>
            {importMsg.map((msg, index) => (
              <div key={index} className={classes.summaryItem}>
                {msg.Result === "Success" && <CheckCircleOutlineIcon className={classes.success} />}
                {msg.Result === "Warning" && <WarningOutlineIcon className={classes.warning} />}
                {msg.Result === "Failure" && <ErrorOutlineIcon className={classes.error} />}
                <Typography key={index} variant="body2" color="textSecondary" className={classes.summaryText}>
                  {msg.Title}
                  {msg.Details && msg.Details.length > 0 ?
                    msg.Details.map(d =>
                      <Typography key={index} className={classes.summaryDetail}>
                        {d}
                      </Typography>
                    )
                    : null}
                </Typography>
              </div>
            ))}
            {result === "Warning" &&
              <>
                <hr className={classes.separator} />
                <div className={classes.warningContainer}>
                  <InfoOutlineIcon fontSize="small" className={classes.info} />
                  <Typography variant="caption" color="textSecondary" className={classes.infoText}>
                    Rows with warnings will be discarded and not imported.
                  </Typography>
                </div>
              </>
            }
          </div>
        </Grid>
      )
    }

    return (
      <Grid item xs={12}>
        <ControlledDragAndDropInput
          buttonText="Select UPC template"
          accept=".xlsx"
          onFileChange={(file) => {
            setValue("File", file, { shouldDirty: true, shouldValidate: true });
          }}
        />
      </Grid>
    )
  }

  const renderButtons = () => {
    if (queueMsg) {
      return (
        <ButtonWithLoadingIndicator
          title="Done"
          onClick={onDone}
        />
      )
    }
    if (importId) {
      return (
        <ButtonWithLoadingIndicator
          title="Queue"
          disabled={queueLoading}
          onClick={onQueue}
        />
      )
    }

    if (importMsg && !isValidated) {
      return (
        <ButtonWithLoadingIndicator
          title="Go Back"
          onClick={resetState}
        />
      )
    }
    return (
      <ButtonWithLoadingIndicator
        title='Validate'
        onClick={onValidate}
        disabled={(!files || files.length === 0) || validateLoading}
        loading={validateLoading}
      />
    )
  }

  return (
    <GeneralModal
      title="Import UPC Template"
      description={
        `Upload an Excel file with UPCs to import them. ${validateLoading ? "It may take a while to process the file, please don't close the window." : ""}`
      }
      visible={isOpen}
      onClose={onClose}
      modalWidth="560px"
    >
      <GeneralModal.ContentArea className={classes.content}>
        <Grid container>
          {renderContent()}
        </Grid>
      </GeneralModal.ContentArea>
      <GeneralModal.ButtonArea>
        {!queueMsg &&
          <Button onClick={onClose}>
            <Typography
              variant="button"
              component="p"
              color="primary">
              Cancel
            </Typography>
          </Button>
        }

        {renderButtons()}
      </GeneralModal.ButtonArea>
    </GeneralModal >
  )
}

const useStyles = makeStyles((theme) => ({
  content: {
    marginTop: 16,
    marginBottom: 32,
    maxHeight: '60vh'
  },
  queueMsg: {
    gap: 8,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  separator: {
    margin: '16px 0',
    border: '1px solid rgba(0,0,0,0.054)'
  },
  summary: {
    marginTop: 32,
    overflow: 'auto',
    maxHeight: 480
  },
  summaryItem: {
    display: 'flex',
    gap: 8,
    marginBottom: 16
  },
  summaryText: {
    marginTop: 2
  },
  summaryDetail: {
    fontSize: 12,
    fontWeight: 400,
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16
  },
  success: {
    color: 'rgb(76, 175, 80)'
  },
  error: {
    color: 'rgb(255, 75, 52)'
  },
  warning: {
    color: 'rgb(255, 152, 0)'
  },
  info: {
    color: 'rgba(0,0,0,0.54)'
  },
  infoText: {
    color: 'rgba(0,0,0,0.85)'
  },
}));